import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · ELTÉRÉS A JELENHEZ — today's value under a past day's tile.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's `ttApplyDelta`: *MOST {value} ↑/↓/=* in muted text under the figure. The direction
 * is read off the two formatted figures, as the mockup does — `1 383:48`, `−6 374 260 Ft`, `47%`
 * — because the tiles hold text, and a tile whose figure is not a number simply gets no arrow.
 *
 * ⚠️ The arrow is a glyph, so it is hidden and its meaning is said in words for a screen reader.
 */
export function KpiDelta({
  now,
  then,
}: {
  readonly now: string | undefined;
  readonly then: string;
}) {
  const t = useT();
  if (now === undefined) return null;
  const a = figureOf(then);
  const b = figureOf(now);
  const direction =
    a === null || b === null || Math.abs(b - a) < 0.0001 ? 'same' : b > a ? 'higher' : 'lower';
  return (
    <div className="text-muted-foreground mt-1.5 flex items-center gap-1 text-[11px] tabular-nums">
      <span>{t('tt.now', { value: now })}</span>
      <span aria-hidden>{direction === 'same' ? '=' : direction === 'higher' ? '↑' : '↓'}</span>
      <span className="sr-only">
        {direction === 'same'
          ? t('tt.same')
          : direction === 'higher'
            ? t('tt.higher')
            : t('tt.lower')}
      </span>
    </div>
  );
}

/** A formatted figure as a number: hours as `H:MM`, a minus as `−`, a decimal comma. */
function figureOf(text: string): number | null {
  const clean = text.replace(/\s/gu, '').replace('−', '-');
  const clock = /^(-?)(\d+):(\d{2})$/u.exec(clean);
  if (clock !== null) {
    const value = Number(clock[2]) + Number(clock[3]) / 60;
    return clock[1] === '-' ? -value : value;
  }
  const number = Number.parseFloat(clean.replace(',', '.').replace(/[^0-9.+-]/gu, ''));
  return Number.isNaN(number) ? null : number;
}
